import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import RevealRenderer from "@/components/RevealRenderer";
import { Button } from "@/components/ui/button";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import { ArrowRight } from "lucide-react";

export default function SampleRevealPage() {
  const [sampleData, setSampleData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/intelligence/sample")
      .then(({ data }) => {
        setSampleData(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <p className="text-sm text-zinc-900">Loading sample Reveal...</p>
      </div>
    );
  }

  let reveal = null;
  if (sampleData?.full_reveal) {
    try {
      reveal = typeof sampleData.full_reveal === "string" ? JSON.parse(sampleData.full_reveal) : sampleData.full_reveal;
    } catch {
      reveal = null;
    }
  }

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="sample-reveal-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" className="block" data-testid="sample-logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="sample-header-logo" />
            </Link>
            <BetaBadge />
          </div>
          <div className="flex items-center gap-4">
            <Link to="/sample" className="text-sm text-zinc-900 hover:text-zinc-950 transition-colors">
              Sample Reveal
            </Link>
            <Link to="/signup" data-testid="sample-get-started-link">
              <Button className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-4 py-2">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="sample-header">
          <p className="text-xs font-medium uppercase tracking-widest text-amber-700 mb-3">Sample Reveal</p>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading mb-1">
            This is what a Kalib AI Reveal looks like.
          </h1>
          <p className="text-[13px] text-zinc-900 mb-3" data-testid="sample-week-identifier">Week of 15–21 April 2026</p>
          {sampleData?.calibrated_for && (
            <p className="text-sm text-zinc-900 leading-relaxed">
              This was calibrated for <span className="font-medium text-zinc-950">{sampleData.calibrated_for.seniority_tier}</span> at a{" "}
              <span className="font-medium text-zinc-950">{sampleData.calibrated_for.company_type}</span> company, focused on{" "}
              <span className="font-medium text-zinc-950">{sampleData.calibrated_for.role_family?.toLowerCase()}</span>.
              Yours will be calibrated to your position and motion at the account.
            </p>
          )}
        </div>

        {/* Signal sentence */}
        {sampleData?.signal_sentence && (
          <div className="bg-white border-l-4 border-l-amber-700 border border-zinc-200 p-6 mb-6" data-testid="sample-signal">
            <p className="text-xs font-medium uppercase tracking-widest text-amber-700 mb-2">Signal this week</p>
            <p className="text-lg md:text-xl font-medium text-zinc-950 leading-relaxed font-heading">
              {sampleData.signal_sentence}
            </p>
          </div>
        )}

        {/* Full reveal */}
        {reveal && <RevealRenderer reveal={reveal} accountName="Deutsche Bank" weekLabel="Week of 15–21 April 2026" />}

        {/* CTA */}
        <div className="bg-white border border-zinc-200 p-8 mt-8 text-center" data-testid="sample-cta">
          <h3 className="text-xl font-medium text-zinc-950 font-heading mb-2">Get your own Reveal — free to start.</h3>
          <p className="text-sm text-zinc-900 mb-6">Calibrated to your vendor position, commercial motion, and seniority level.</p>
          <Link to="/signup" data-testid="sample-cta-btn">
            <Button className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-6 py-3 h-auto">
              Get started free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
